import { useNavigate, useLocation } from "react-router-dom";
import {
  Home,
  Users,
  FileText,
  CreditCard,
  BarChart2,
  Settings,
  ChevronRight,
  BookOpen,
  ArrowRightLeft,
  BarChart,
  HelpCircle,
  LogOut,
  Menu,
  X,
} from 'lucide-react';
import { useState } from "react";

export default function PremiumNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Organized menu categories with appropriate icons
  const mainMenuItems = [
    { name: 'Dashboard', icon: Home, path: '/dashboard', badge: null },
    { name: 'Students', icon: Users, path: '/students', badge: null },
    { name: 'Bulk Import', icon: Users, path: '/BulkImport', badge: 'New' },
  ];

  const onSignOut = () => {
    localStorage.removeItem('token');
    navigate('/');
  };

  const financialItems = [
    { name: 'Invoices', icon: FileText, path: '/invoices', badge: null },
    { name: 'Expenses', icon: FileText, path: '/expenses', badge: null },
    { name: 'Payments', icon: CreditCard, path: '/payments', badge: null },
    { name: 'Fee Voteheads', icon: BookOpen, path: '/Voteheads/Fee', badge: null },
    { name: 'Expense Voteheads', icon: BookOpen, path: '/Voteheads/Expense', badge: null },
    { name: 'Chart Of Accounts', icon: BarChart, path: '/chart-of-accounts', badge: null },
    { name: 'Fee Balances', icon: BarChart2, path: '/balances', badge: null },
    { name: 'Journal Entries', icon: BookOpen, path: '/journals', badge: null },
    { name: 'Trial Balances', icon: ArrowRightLeft, path: '/trial-balance', badge: null },
  ];

  const payrollItems = [
    { name: 'Salaries & Benefits', icon: FileText, path: '/salaries', badge: null },
    { name: 'Payroll', icon: Settings, path: '/payroll', badge: null },
  ];

  const supportItems = [
    { name: 'Help & Support', icon: HelpCircle, path: '/help', badge: null },
  ];

  // Function to render menu item
  const renderMenuItem = (item, index) => {
    const Icon = item.icon;
    const isActive = currentPath === item.path;

    return (
      <div
        key={item.name}
        onClick={() => navigate(item.path)}
        className={`cursor-pointer flex items-center px-4 py-3 rounded-xl transition-all duration-300 group relative overflow-hidden ${
          isActive
            ? 'bg-slate-800 text-emerald-400 font-medium shadow-2xl shadow-emerald-900/30 '
            : 'text-slate-300 hover:bg-slate-800/70 hover:text-white hover:shadow-lg hover:shadow-slate-900/20 hover:border-slate-700/50 border border-transparent'
        }`}
      >
        {/* Animated background glow for active item */}
        {isActive && (
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 via-purple-600/20 to-violet-600/20 animate-pulse" />
        )}

        <div
          className={`flex items-center justify-center relative z-10 ${
            isActive ? 'text-white' : 'text-slate-400 group-hover:text-emerald-400'
          }`}
        >
          <Icon size={isCollapsed ? 20 : 18} strokeWidth={isActive ? 2.5 : 2} />
        </div>

        {!isCollapsed && (
          <>
            <span className="ml-3 flex-grow text-sm font-medium relative z-10">{item.name}</span>

            {item.badge && (
              <span
                className={`text-xs px-2 py-1 rounded-full font-semibold relative z-10 ${
                  item.badge === 'New'
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg shadow-emerald-500/25'
                    : isActive
                    ? 'bg-white/20 text-white backdrop-blur-sm'
                    : 'bg-slate-700 text-slate-300 group-hover:bg-slate-600'
                }`}
              >
                {item.badge}
              </span>
            )}

            {isActive && <ChevronRight size={16} className="text-white/70 relative z-10" />}
          </>
        )}

        {/* Enhanced tooltip for collapsed mode */}
        {isCollapsed && (
          <div className="absolute left-full ml-4 z-30 origin-left scale-0 px-4 py-3 rounded-xl bg-slate-900 text-white text-sm font-medium opacity-0 shadow-2xl shadow-slate-900/50 border border-slate-700/50 transition-all duration-300 group-hover:scale-100 group-hover:opacity-100 whitespace-nowrap backdrop-blur-sm">
            <div className="flex items-center">
              {item.name}
              {item.badge && (
                <span className="ml-2 text-xs px-2 py-1 rounded-full bg-emerald-500 text-white">
                  {item.badge}
                </span>
              )}
            </div>
            {/* Tooltip arrow */}
            <div className="absolute top-1/2 -left-1 w-2 h-2 bg-slate-900 border-l border-b border-slate-700/50 transform -translate-y-1/2 rotate-45" />
          </div>
        )}
      </div>
    );
  };

  return (
    <nav
      className={`bg-slate-900 shadow-2xl flex flex-col transition-all duration-300 border-r border-slate-800/50 ${
        isCollapsed ? 'w-20' : 'w-72'
      }`}
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #010132 0%, #010132 50%, #000019 100%)',
        boxShadow: '',
      }}
    >
      {/* Header with collapse toggle */}
     

      {/* Menu sections */}
      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent px-4 py-6">
        {/* Main Menu */}
        <div className="space-y-2 mb-8">
          {!isCollapsed && (
            <h6 className="text-xs font-bold text-slate-400 ml-4 mb-4 uppercase tracking-wider flex items-center">
              <div className="w-4 h-0.5 bg-emerald-500 mr-2 rounded-full" />
              Main Menu
            </h6>
          )}
          {mainMenuItems.map(renderMenuItem)}
        </div>

        {/* Financial */}
        <div className="space-y-2 mb-8">
          {!isCollapsed && (
            <h6 className="text-xs font-bold text-slate-400 ml-4 mb-4 uppercase tracking-wider flex items-center">
              <div className="w-4 h-0.5 bg-teal-500 mr-2 rounded-full" />
              Financial
            </h6>
          )}
          {financialItems.map(renderMenuItem)}
        </div>

        {/* Payroll */}
        <div className="space-y-2 mb-8">
          {!isCollapsed && (
            <h6 className="text-xs font-bold text-slate-400 ml-4 mb-4 uppercase tracking-wider flex items-center">
              <div className="w-4 h-0.5 bg-cyan-500 mr-2 rounded-full" />
              Payroll
            </h6>
          )}
          {payrollItems.map(renderMenuItem)}
        </div>

        {/* Support 
        <div className="space-y-2">
          {!isCollapsed && (
            <h6 className="text-xs font-bold text-slate-400 ml-4 mb-4 uppercase tracking-wider flex items-center">
              <div className="w-4 h-0.5 bg-purple-500 mr-2 rounded-full" />
              Support
            </h6>
          )}
          {supportItems.map(renderMenuItem)}
        </div>
        */}
      </div>

      {/* Footer with logout */}
      <div className="p-4 border-t border-slate-800/50">
        <div
          className="cursor-pointer flex items-center px-4 py-3 rounded-xl transition-all duration-300 group text-slate-400 hover:bg-red-900/20 hover:text-red-400 hover:border-red-800/50 border border-transparent"
          onClick={onSignOut}
        >
          <LogOut size={18} strokeWidth={2} />
          {!isCollapsed && (
            <span className="ml-3 text-sm font-medium">Logout</span>
          )}
          {isCollapsed && (
            <div className="absolute left-full ml-4 z-30 origin-left scale-0 px-4 py-3 rounded-xl bg-slate-900 text-white text-sm font-medium opacity-0 shadow-2xl shadow-slate-900/50 border border-slate-700/50 transition-all duration-300 group-hover:scale-100 group-hover:opacity-100 whitespace-nowrap backdrop-blur-sm">
              Logout
              <div className="absolute top-1/2 -left-1 w-2 h-2 bg-slate-900 border-l border-b border-slate-700/50 transform -translate-y-1/2 rotate-45" />
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}